import React from 'react'
import { Aircraft, Event } from '../../../types/intellijetnet'
import { AircraftOverride, TakePriceModifier } from '../../../types/database'
import { OffMarket } from '../../../types/database'
import { formatAskingPrice, formatDate } from '../../../utils/formatters'
import { PriceWithModifier } from '../../shared/PriceWithModifier'
import InlineAskPriceEditor from '../../inline-editors/InlineAskPriceEditor'
import InlineTakePriceEditor from '../../inline-editors/InlineTakePriceEditor'
import type { EditingCell, EditingCellFieldName } from '../types'

interface RemovedPricingCellsProps {
  aircraft: Aircraft
  effectiveAircraft: { asking?: string | number | null; take_price?: number | null }
  isEditMode?: boolean
  cellBorderClass: string
  isOffMarket: boolean
  overrides: Map<number, AircraftOverride>
  editingCell: EditingCell | null
  event?: Event | null
  offMarket?: OffMarket | null
  hideExternalColumns: boolean
  handleStartEdit: (aircraftId: number, fieldName: EditingCellFieldName) => void
  handleCancelEdit: () => void
  handleAskPriceSave: (aircraftId: number, priceFieldName: 'asking_price' | 'external_asking_price', newValue: number | null, newMakeOffer: boolean | null) => Promise<void>
  handleTakePriceSave: (aircraftId: number, priceFieldName: 'take_price' | 'external_take_price', newValue: number | null, newModifier: TakePriceModifier | null) => Promise<void>
}

/**
 * Renders the removed-specific pricing columns: Date Removed and External prices.
 */
export function RemovedPricingCells(props: RemovedPricingCellsProps) {
  const { aircraft, effectiveAircraft, isEditMode, cellBorderClass, isOffMarket, overrides, editingCell,
    event, offMarket, hideExternalColumns,
    handleStartEdit, handleCancelEdit, handleAskPriceSave, handleTakePriceSave } = props

  const override = overrides.get(aircraft.aircraft_id)
  const ec = (field: EditingCellFieldName) => editingCell?.aircraftId === aircraft.aircraft_id && !editingCell?.transactionId && editingCell?.fieldName === field
  const editClass = (hasOvr: boolean) => `${hasOvr ? 'text-red-800 font-medium' : ''} ${isEditMode ? 'cursor-pointer hover:bg-green-50 rounded px-1 -mx-1' : ''}`
  const startEdit = (field: EditingCellFieldName) => isEditMode ? (e: React.MouseEvent) => { e.stopPropagation(); handleStartEdit(aircraft.aircraft_id, field); } : undefined

  return (
    <>
      {/* Date Removed */}
      <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
        {isOffMarket ? formatDate(offMarket?.created_at) : formatDate(event?.date)}
      </td>

      {/* External Prices */}
      {!hideExternalColumns && (
        <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
          {/* Adj Ask - editable */}
          <div
            className={editClass(override?.external_asking_price != null || override?.adj_ask_make_offer != null)}
            onClick={startEdit('external_asking_price')}
          >
            {ec('external_asking_price') ? (
              <InlineAskPriceEditor
                value={override?.external_asking_price ?? null}
                makeOffer={override?.adj_ask_make_offer ?? null}
                fieldName="external_asking_price"
                onSave={(v, mo) => handleAskPriceSave(aircraft.aircraft_id, 'external_asking_price', v, mo)}
                onCancel={handleCancelEdit}
              />
            ) : (
              override?.adj_ask_make_offer
                ? 'M.O.'
                : formatAskingPrice(override?.external_asking_price ?? effectiveAircraft.asking)
            )}
          </div>
          {/* Adj Take - editable */}
          <div
            className={editClass(override?.external_take_price != null || override?.external_take_price_modifier != null)}
            onClick={startEdit('external_take_price')}
          >
            {ec('external_take_price') ? (
              <InlineTakePriceEditor
                value={override?.external_take_price ?? null}
                modifier={override?.external_take_price_modifier ?? null}
                fieldName="external_take_price"
                onSave={(v, m) => handleTakePriceSave(aircraft.aircraft_id, 'external_take_price', v, m)}
                onCancel={handleCancelEdit}
              />
            ) : (
              <PriceWithModifier
                value={override?.external_take_price ?? effectiveAircraft.take_price}
                modifier={override?.external_take_price_modifier ?? override?.take_price_modifier}
              />
            )}
          </div>
        </td>
      )}
    </>
  )
}

